import { useState, useRef, useEffect, useCallback, useMemo } from 'react'
import { motion } from 'framer-motion'
import {
  forceSimulation,
  forceManyBody,
  forceCenter,
  forceCollide,
  forceX,
  forceY,
  forceRadial,
  forceLink,
  type SimulationNodeDatum,
  type SimulationLinkDatum,
} from 'd3-force'
import { PersonaIcon } from './PersonaIcon'
import { HierarchicalLayout } from './HierarchicalLayout'
import { useTheme } from '../contexts/ThemeContext'
import { getPersonaDisplayName, PERSONA_GLOW_COLORS } from '../constants/personas'

export interface Worker {
  id: string
  name: string
  persona: string  // nexus, voyager, architect, prism, visionary, demiurge, terraformer, weaver, sentinel, etc.
  state: 'active' | 'inactive'  // active = has recent heartbeat, inactive = no heartbeat
}

export interface Shard {
  id: string
  type: 'SPEC' | 'TASK'
  status: string  // TODO, READY, IN_PROGRESS, DONE, BLOCKED, REVIEW, etc.
  assignee: string | null
  dependencies: string[]
}

export interface GraphViewProps {
  workers: Worker[]
  shards: Shard[]
  onWorkerClick?: (workerId: string) => void
  onShardClick?: (shardId: string) => void
}

type LayoutMode = 'force-directed' | 'hierarchical'

// Node types for force simulation
type NodeType = 'worker' | 'shard'

interface ForceNode extends SimulationNodeDatum {
  id: string
  type: NodeType
  data: Worker | Shard
  radius: number
  fx?: number | null  // Fixed x position (for pinning Nexus)
  fy?: number | null  // Fixed y position (for pinning Nexus)
}

// Link type for shard-to-worker connections
interface ForceLink extends SimulationLinkDatum<ForceNode> {
  source: string | ForceNode
  target: string | ForceNode
}

const STATUS_COLORS: Record<string, string> = {
  // Uppercase (from backend)
  TODO: 'text-cosmic-star',
  READY: 'text-cyan-400',
  IN_PROGRESS: 'text-teal-400',
  REVIEW: 'text-purple-400',
  DONE: 'text-gray-400',
  BLOCKED: 'text-cosmic-plasma',
  // Lowercase (legacy)
  draft: 'text-cosmic-star',
  ready: 'text-cyan-400',
  in_progress: 'text-teal-400',
  complete: 'text-gray-400',
  blocked: 'text-cosmic-plasma',
}

// Force simulation parameters
const FORCE_PARAMS = {
  chargeStrength: -400, // Repulsion force between nodes
  centerStrength: 0.05, // Strength of centering force
  collisionRadius: 1.2, // Multiplier for collision detection
  xStrength: 0.1, // Horizontal positioning strength
  yStrength: 0.1, // Vertical positioning strength
  alphaDecay: 0.025, // How quickly the simulation cools down (slightly faster settling)
  velocityDecay: 0.5, // Friction/damping (increased to reduce oscillation)
  workerRadialStrength: 0.8, // Strength pushing workers to outer ring
  shardLinkStrength: 0.6, // Strength pulling shards toward their assigned worker
  shardLinkDistance: 60, // Target distance between shard and assigned worker
}

// Node sizes
const WORKER_RADIUS = 48 // Half of the 96px worker node
const SHARD_RADIUS = 20 // Half of the shard particle size

// Hook to manage force simulation
function useForceSimulation(
  workers: Worker[],
  shards: Shard[],
  containerWidth: number,
  containerHeight: number
) {
  const [nodePositions, setNodePositions] = useState<Map<string, { x: number; y: number }>>(new Map())
  const simulationRef = useRef<ReturnType<typeof forceSimulation<ForceNode>> | null>(null)

  // Force graph: only show active workers + nexus (always visible)
  const filteredWorkers = useMemo(() =>
    workers.filter(
      (worker) => worker.persona === 'nexus' || worker.state === 'active'
    ),
    [workers]
  )

  // Create nodes from workers and shards
  const createNodes = useCallback((): ForceNode[] => {
    // Don't create nodes until container is sized - prevents shards at top-left
    if (containerWidth === 0 || containerHeight === 0) {
      return []
    }

    const nodes: ForceNode[] = []
    const centerX = containerWidth / 2
    const centerY = containerHeight / 2
    const outerRadius = Math.min(containerWidth, containerHeight) * 0.35

    // Add worker nodes
    filteredWorkers.forEach((worker) => {
      const isNexus = worker.persona === 'nexus'

      if (isNexus) {
        // Nexus is pinned at center with fx/fy
        nodes.push({
          id: `worker-${worker.id}`,
          type: 'worker',
          data: worker,
          radius: WORKER_RADIUS,
          x: centerX,
          y: centerY,
          fx: centerX, // Fixed x position
          fy: centerY, // Fixed y position
        })
      } else {
        // Other workers start in a circular distribution on the outer ring
        // Skip nexus in the index calculation for even distribution
        const nonNexusWorkers = filteredWorkers.filter((w) => w.persona !== 'nexus')
        const nonNexusIndex = nonNexusWorkers.findIndex((w) => w.id === worker.id)
        const angle = (nonNexusIndex / Math.max(nonNexusWorkers.length, 1)) * 2 * Math.PI
        nodes.push({
          id: `worker-${worker.id}`,
          type: 'worker',
          data: worker,
          radius: WORKER_RADIUS,
          x: centerX + Math.cos(angle) * outerRadius,
          y: centerY + Math.sin(angle) * outerRadius,
        })
      }
    })

    // Add shard nodes - position them near their assigned worker initially
    shards.forEach((shard, index) => {
      // Find the assigned worker node
      const assignedWorkerId = shard.assignee
      const assignedWorkerNode = assignedWorkerId
        ? nodes.find((n) => n.id === `worker-${assignedWorkerId}`)
        : null

      let initialX: number
      let initialY: number

      if (assignedWorkerNode) {
        // Position near assigned worker with some offset
        const offsetAngle = (index / Math.max(shards.length, 1)) * 2 * Math.PI
        const offsetRadius = 40 + Math.random() * 30
        initialX = (assignedWorkerNode.x ?? centerX) + Math.cos(offsetAngle) * offsetRadius
        initialY = (assignedWorkerNode.y ?? centerY) + Math.sin(offsetAngle) * offsetRadius
      } else {
        // Unassigned shards go near the center (near Nexus)
        const offsetAngle = (index / Math.max(shards.length, 1)) * 2 * Math.PI
        const offsetRadius = 80 + Math.random() * 40
        initialX = centerX + Math.cos(offsetAngle) * offsetRadius
        initialY = centerY + Math.sin(offsetAngle) * offsetRadius
      }

      nodes.push({
        id: `shard-${shard.id}`,
        type: 'shard',
        data: shard,
        radius: SHARD_RADIUS,
        x: initialX,
        y: initialY,
      })
    })

    return nodes
  }, [filteredWorkers, shards, containerWidth, containerHeight])

  // Create links between shards and their assigned workers
  const createLinks = useCallback((nodes: ForceNode[]): ForceLink[] => {
    const links: ForceLink[] = []

    shards.forEach((shard) => {
      if (shard.assignee) {
        const workerNodeId = `worker-${shard.assignee}`
        const shardNodeId = `shard-${shard.id}`

        // Only create link if both nodes exist
        const workerExists = nodes.some((n) => n.id === workerNodeId)
        const shardExists = nodes.some((n) => n.id === shardNodeId)

        if (workerExists && shardExists) {
          links.push({
            source: shardNodeId,
            target: workerNodeId,
          })
        }
      }
    })

    return links
  }, [shards])

  useEffect(() => {
    if (containerWidth === 0 || containerHeight === 0) return

    // Stop any existing simulation
    if (simulationRef.current) {
      simulationRef.current.stop()
    }

    const nodes = createNodes()
    if (nodes.length === 0) {
      setNodePositions(new Map())
      return
    }

    const links = createLinks(nodes)
    const centerX = containerWidth / 2
    const centerY = containerHeight / 2
    const outerRadius = Math.min(containerWidth, containerHeight) * 0.35

    // Create the force simulation
    const simulation = forceSimulation<ForceNode>(nodes)
      .force('charge', forceManyBody<ForceNode>()
        .strength((d) => {
          // Weaker repulsion for shards, stronger for workers
          return d.type === 'shard' ? -100 : FORCE_PARAMS.chargeStrength
        })
      )
      .force('center', forceCenter<ForceNode>(centerX, centerY)
        .strength(FORCE_PARAMS.centerStrength)
      )
      .force('collision', forceCollide<ForceNode>()
        .radius((d) => d.radius * FORCE_PARAMS.collisionRadius)
        .strength(0.8)
      )
      // Radial force to push workers to the outer ring (except Nexus which is pinned)
      .force('radial', forceRadial<ForceNode>(
        (d) => {
          // Nexus stays at center (radius 0), workers go to outer ring, shards stay flexible
          if (d.type === 'worker') {
            const worker = d.data as Worker
            return worker.persona === 'nexus' ? 0 : outerRadius
          }
          return 0 // Shards don't have radial force - they're pulled by links
        },
        centerX,
        centerY
      ).strength((d) => {
        if (d.type === 'worker') {
          const worker = d.data as Worker
          return worker.persona === 'nexus' ? 0 : FORCE_PARAMS.workerRadialStrength
        }
        return 0
      }))
      // Link force to pull shards toward their assigned workers
      .force('link', forceLink<ForceNode, ForceLink>(links)
        .id((d) => d.id)
        .distance(FORCE_PARAMS.shardLinkDistance)
        .strength(FORCE_PARAMS.shardLinkStrength)
      )
      // Gentle centering force for unassigned shards
      .force('x', forceX<ForceNode>(centerX)
        .strength((d) => {
          if (d.type === 'shard') {
            const shard = d.data as Shard
            return shard.assignee ? 0 : 0.05 // Pull unassigned shards toward center
          }
          return 0
        })
      )
      .force('y', forceY<ForceNode>(centerY)
        .strength((d) => {
          if (d.type === 'shard') {
            const shard = d.data as Shard
            return shard.assignee ? 0 : 0.05 // Pull unassigned shards toward center
          }
          return 0
        })
      )
      .alphaDecay(FORCE_PARAMS.alphaDecay)
      .velocityDecay(FORCE_PARAMS.velocityDecay)

    // Throttle state updates with requestAnimationFrame to avoid 60+ re-renders/second
    let rafId: number | null = null
    let latestPositions: Map<string, { x: number; y: number }> | null = null

    // Update positions on each tick
    simulation.on('tick', () => {
      const newPositions = new Map<string, { x: number; y: number }>()

      nodes.forEach((node) => {
        // Clamp positions to container bounds with padding
        const padding = node.radius + 20
        const x = Math.max(padding, Math.min(containerWidth - padding, node.x ?? 0))
        const y = Math.max(padding, Math.min(containerHeight - padding, node.y ?? 0))

        // Update node position (for simulation) - but respect fixed positions
        if (node.fx === undefined || node.fx === null) {
          node.x = x
        }
        if (node.fy === undefined || node.fy === null) {
          node.y = y
        }

        newPositions.set(node.id, { x: node.x ?? x, y: node.y ?? y })
      })

      latestPositions = newPositions

      // Only update React state once per animation frame
      if (rafId !== null) return
      rafId = requestAnimationFrame(() => {
        if (latestPositions) {
          setNodePositions(new Map(latestPositions))
        }
        rafId = null
      })
    })

    simulationRef.current = simulation

    // Cleanup
    return () => {
      simulation.stop()
      if (rafId !== null) {
        cancelAnimationFrame(rafId)
      }
    }
  }, [createNodes, createLinks, containerWidth, containerHeight])

  // Reheat simulation when data changes
  useEffect(() => {
    if (simulationRef.current) {
      simulationRef.current.alpha(0.3).restart()
    }
  }, [filteredWorkers.length, shards.length])

  return { nodePositions, filteredWorkers }
}

export function GraphView({
  workers,
  shards,
  onWorkerClick,
  onShardClick,
}: GraphViewProps) {
  const [layoutMode, setLayoutMode] = useState<LayoutMode>('hierarchical')
  const containerRef = useRef<HTMLDivElement>(null)
  const [containerSize, setContainerSize] = useState({ width: 0, height: 0 })
  const { theme } = useTheme()
  const isCorporate = theme === 'corporate'

  // Track container size
  useEffect(() => {
    const container = containerRef.current
    if (!container) return

    const updateSize = () => {
      setContainerSize({
        width: container.clientWidth,
        height: container.clientHeight,
      })
    }

    // Initial size
    updateSize()

    // Watch for resize
    const resizeObserver = new ResizeObserver(updateSize)
    resizeObserver.observe(container)

    return () => resizeObserver.disconnect()
  }, [])

  // Filter shards for force graph - hide DONE tasks (but show all in hierarchy)
  // Filter shards for force graph - memoize to prevent infinite re-renders
  const forceShards = useMemo(() => shards.filter((s) => s.status !== 'DONE' && s.status !== 'DECOMPOSED'), [shards])

  // Memoize inputs to force simulation to prevent infinite re-renders
  const forceWorkers = useMemo(
    () => layoutMode === 'force-directed' ? workers : [],
    [layoutMode, workers]
  )
  const forceShardsInput = useMemo(
    () => layoutMode === 'force-directed' ? forceShards : [],
    [layoutMode, forceShards]
  )

  // Get positions from force simulation
  const { nodePositions, filteredWorkers } = useForceSimulation(
    forceWorkers,
    forceShardsInput,
    containerSize.width,
    containerSize.height
  )

  const toggleLayout = () => {
    setLayoutMode((prev) =>
      prev === 'force-directed' ? 'hierarchical' : 'force-directed'
    )
  }

  const renderGhostOutlines = () => {
    if (workers.length > 0) return null

    const defaultPersonas = ['nexus', 'voyager', 'architect', 'prism', 'visionary', 'cantor', 'chaos']

    return (
      <div className="ghost-outlines flex flex-wrap gap-8 justify-center items-center h-full p-8">
        {defaultPersonas.map((persona) => (
          <div
            key={persona}
            data-testid={`ghost-outline-${persona}`}
            className="ghost-outline relative opacity-20 border-2 border-dashed border-cosmic-border rounded-full w-24 h-24 flex items-center justify-center"
          >
            <PersonaIcon persona={persona} size="xl" className="opacity-50" />
            <div className="absolute -bottom-6 text-xs text-cosmic-border capitalize">
              {getPersonaDisplayName(persona, isCorporate)}
            </div>
          </div>
        ))}
      </div>
    )
  }

  const getStatusColor = (status: string): string => {
    return STATUS_COLORS[status] || STATUS_COLORS[status.toUpperCase()] || 'text-gray-400'
  }

  const renderForceLayout = () => {
    return (
      <>
        {/* Worker nodes with force-directed positions */}
        {filteredWorkers.map((worker) => {
          const pos = nodePositions.get(`worker-${worker.id}`)
          if (!pos) return null

          const glowColor = PERSONA_GLOW_COLORS[worker.persona?.toLowerCase() || 'nexus'] || 'rgba(59, 130, 246, 0.5)'
          const stateClass = worker.state === 'active'
            ? ''
            : 'opacity-50'

          return (
            <motion.div
              key={worker.id}
              data-testid={`worker-node-${worker.id}`}
              className={`worker-node state-${worker.state} absolute cursor-pointer`}
              style={{
                transform: 'translate(-50%, -50%)',
              }}
              animate={{
                left: pos.x,
                top: pos.y,
              }}
              transition={{
                type: 'tween',
                duration: 0.05,  // Very short duration for smoothness without lag
                ease: 'linear',
              }}
              onClick={() => onWorkerClick?.(worker.id)}
            >
              <div className="relative">
                {/* Heartbeat pulse animation - only for active workers */}
                {worker.state === 'active' && (
                  <div className="heartbeat-pulse absolute -inset-2 rounded-full bg-cosmic-nebula opacity-20 animate-pulse-slow" />
                )}

                {/* Worker icon and name */}
                <div
                  className={`relative z-10 flex flex-col items-center justify-center p-4 rounded-full w-24 h-24 transition-all ${stateClass}`}
                  // Glow via drop-shadow (follows the icon's alpha shape) rather than
                  // box-shadow (which traced the invisible round container → a hard
                  // circular halo floating off the icon).
                  style={worker.state === 'active' ? { filter: `drop-shadow(0 0 6px ${glowColor}) drop-shadow(0 0 10px ${glowColor})` } : undefined}
                >
                  <PersonaIcon persona={worker.persona} size="xl" />
                </div>
              </div>
              <div className="text-center mt-2 text-sm font-medium">{worker.name}</div>
              <div className="text-center text-xs text-cosmic-border capitalize">{getPersonaDisplayName(worker.persona, isCorporate)}</div>
            </motion.div>
          )
        })}

        {/* Shard particles with force-directed positions (excludes DONE) */}
        {forceShards.map((shard) => {
          const pos = nodePositions.get(`shard-${shard.id}`)
          if (!pos) return null

          const isSpec = shard.type === 'SPEC' || shard.id.startsWith('SPEC')
          const colorClass = getStatusColor(shard.status)

          // Calculate center position (where Nexus is)
          const centerX = containerSize.width / 2
          const centerY = containerSize.height / 2

          return (
            <motion.div
              key={shard.id}
              data-testid={`shard-particle-${shard.id}`}
              className={`shard-particle ${colorClass} absolute cursor-pointer transition-all hover:scale-110`}
              style={{
                transform: 'translate(-50%, -50%)',
              }}
              initial={{
                left: centerX,  // Start from center (Nexus position)
                top: centerY,
              }}
              animate={{
                left: pos.x,
                top: pos.y,
              }}
              transition={{
                type: 'tween',
                duration: 0.05,  // Very short duration for smoothness without lag
                ease: 'linear',
              }}
              onClick={() => onShardClick?.(shard.id)}
              title={`${shard.id}: ${shard.status}`}
            >
              <div className={`flex items-center justify-center text-lg font-mono border-2 border-current rounded ${isSpec ? 'w-10 h-10' : 'w-8 h-8 rounded-full'}`}>
                {isSpec ? '⬡' : '●'}
              </div>
              <div className="text-xs mt-1 text-center opacity-70">{shard.id}</div>
            </motion.div>
          )
        })}
      </>
    )
  }

  const isEmpty = workers.length === 0 && shards.length === 0

  return (
    <div
      data-testid="graph-view"
      className={`graph-view layout-${layoutMode} relative w-full h-full bg-cosmic-abyss overflow-hidden flex flex-col min-h-0`}
    >
      {/* Header with layout toggle */}
      <div className="absolute top-4 right-4 z-10">
        <button
          onClick={toggleLayout}
          className="px-4 py-2 bg-cosmic-surface border border-cosmic-border rounded-lg hover:border-cosmic-nebula transition-colors text-sm font-medium"
          aria-label={`Toggle layout: ${layoutMode}`}
        >
          Layout: {layoutMode === 'force-directed' ? 'Force' : 'Hierarchical'}
        </button>
      </div>

      {/* Main graph area */}
      <div
        ref={containerRef}
        className="graph-container relative w-full p-4 flex-1 min-h-0 max-lg:overflow-auto"
      >
        {layoutMode === 'hierarchical' ? (
          <HierarchicalLayout
            workers={workers}
            shards={shards}
            onWorkerClick={onWorkerClick}
            onShardClick={onShardClick}
          />
        ) : isEmpty ? (
          <div className="flex flex-col items-center justify-center h-full">
            <div className="text-center mb-8">
              <div className="text-cosmic-border text-sm mb-4">
                No workers or shards yet
              </div>
              <div className="text-cosmic-border text-xs opacity-70">
                Ghost outlines show where personas will appear
              </div>
            </div>
            {renderGhostOutlines()}
          </div>
        ) : (
          <>
            {/* Empty state ghost outlines */}
            {workers.length === 0 && renderGhostOutlines()}

            {/* Force-directed layout with absolute positioning */}
            {renderForceLayout()}
          </>
        )}
      </div>
    </div>
  )
}
